import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { io, Socket } from "socket.io-client";
import { Constants } from "constants/Constants";
import { useAuth } from "../../hooks/useAuth";
import { getStoredData } from "../../utils/fnAsyncStorage";

type Props = {
  children?: React.ReactNode;
};

const SocketContext = createContext<object | null>(null);

export type Listener = {
  events: string[];
  handleEvent: (notificationString: string, update: object) => void;
};

let listeners = [] as Listener[];

export const addEventListener = (listener: Listener) => {
  listeners.push(listener);
};
export const removeEventListener = (listener: Listener) => {
  listeners = listeners.filter((l) => l !== listener);
};

const emptyValues = {};

export function SocketProvider({ children }: Props) {
  const apiUrl = Constants.ROOT_URL;

  const [socket, setSocket] = useState<Socket>();
  const { user } = useAuth();

  const authenticate = useCallback(async () => {
    const token = await getStoredData("token");
    if (socket && user?.id && token) {
      socket.emit("authenticate", { token });
    }
  }, [socket, user?.id]);

  // init socket
  useEffect(() => {
    setSocket(io(apiUrl));
  }, [apiUrl]);

  // setAlert not available the first time, so we have to reset events callback
  useEffect(() => {
    if (socket) {
      socket.off("Notification");
      socket.on("Notification", (eventType: string, data: object) => {
        listeners
          .filter((l) => l.events.includes(eventType))
          .forEach((listener) => {
            listener.handleEvent(eventType, data);
          });
      });

      socket.off("reconnect");
      socket.on("reconnect", async () => {
        authenticate();
      });
    }
  }, [user, authenticate, socket]);

  // if user is logged in, authenticate socket
  useEffect(() => {
    if (user?.id) {
      authenticate();
    }
  }, [user?.id, authenticate]);

  return (
    <SocketContext.Provider value={emptyValues}>
      {children}
    </SocketContext.Provider>
  );
}

const useSocket = () => useContext(SocketContext);

export default useSocket;
